import {FormattedMessageWrapperProps} from '../../localization/components/FormattedMessage'
import {FilterId} from '../../search/types'

/**
 * Represents all possible brand codes in the system.
 * Used to identify different brands and their configurations.
 */
export const brandCodes = [
  'bookaway',
  'checkmybus',
  'dealbase',
  'demo',
  'demoAdvanced',
  'demoBasic',
  'demoCustom',
  'etrip',
  'findhotel',
  'goseek',
  'holidaypiratesde',
  'kayak',
  'kiwi',
  'vio',
  'weward',
  'ovago'
] as const

/**
 * Union type of all valid brand codes.
 * This type is automatically derived from the `brandCodes` array to ensure type safety
 * and avoid duplication of brand values.
 */
export type BrandCode = (typeof brandCodes)[number]

/**
 * Represents the theme name for each brand.
 * Used for styling and UI customization.
 */
export type BrandThemeName =
  | 'bookaway'
  | 'checkmybus'
  | 'dealbase'
  | 'goseek'
  | 'holidaypirates'
  | 'kayak'
  | 'kiwi'
  | 'vio'
  | 'weward'
  | 'ovago'

/**
 * Type alias for brand-specific messages that need to be formatted.
 */
export type BrandMessage = FormattedMessageWrapperProps

/**
 * Represents a brand's configuration and features.
 * Contains all necessary information to customize the application for a specific brand.
 */
export interface Brand {
  /** Unique identifier for the brand */
  code: BrandCode
  /** Customer IO identifier for the brand */
  customerIoSiteId?: string
  /**
   * Alternative identifier for hostname matching. Takes precedence over brandCode when resolving
   * brand from URL hostname parts (e.g., 'urlaubspiraten' and `wewardapp`).
   * Enables brand identification when URL structure differs from brand code.
   */
  domainCode?: string
  /** Display name of the brand */
  name: string
  /** Main URL addresses for the brand */
  siteAddresses: {
    /** Home, search results and accommodation page URL address for the brand */
    address: string
    /** URL for the checkout process. Optional because not all brands are BoB  */
    checkoutAddress?: string
    /** URL for booking management. Optional because not all brands are BoB  */
    bookingManagementAddress?: string
    /**
     * Custom URL to redirect to when clicking the brand logo. If not provided, defaults to logoUrl or address
     * Ignored by the Checkmybus brand due to custom logic
     */
    logoRedirectUrl?: string
  }
  /** Provider code used to get the logo for the AP and the redirect page */
  providerCode: string
  /** Brand level determining feature availability */
  level: 'basic' | 'advanced' | 'custom'
  /** Whether private/locked offers are locked by default */
  offersLockedByDefault: boolean
  /** Whether this is an internal brand, e.g., GoSeek, Dealbase, Etrip*/
  isInternal: boolean
  /** Whether the brand is live and active */
  isLive: boolean
  /** Whether to show sign-in functionality */
  showSignIn: boolean
  /**
   * Used to control how offer redirects are handled when a user clicks on an offer.
   * It determines whether to show an intermediate redirect page or directly redirect to the external provider's URL
   */
  hideInternalRedirect: boolean
  /** Brand tagline for marketing */
  tagline: string
  /** Meta description for SEO */
  metaDescription: string
  /** Theme name for styling that is used by Atlas */
  theme: BrandThemeName
  /** Messages for the brand */
  messages: {
    /** Page title message */
    pageTitleMessage: BrandMessage
    /** Page title tagline message */
    pageTitleTagLineMessage: BrandMessage
    /** Homepage heading message */
    homepageHeadingMessage: BrandMessage
    /** Homepage subheading message   */
    homepageSubheadingMessage?: BrandMessage
    /** Message for private deals */
    privateDealMessage: BrandMessage
    /** Widget header message   */
    widgetHeaderMessage?: BrandMessage
  }
  /** Cookie consent identifier   */
  cookieConsentId?: string
  /** Additional meta tags */
  meta?: {
    'facebook-domain-verification'?: string
  }
  /** Supporting links that are displayed across the page e.g. the footer */
  links: {
    /** About us page URL   */
    aboutUsUrl?: string
    /** Careers page URL   */
    careersUrl?: string
    /** Developers page URL   */
    developersUrl?: string
    /** Partnerships page URL   */
    partnershipsUrl?: string
    /** Blog URL   */
    blogUrl?: string
    /** Help page URL   */
    helpUrl?: string
    /** Privacy policy URL */
    privacyPolicyUrl: string
    /** Terms of use URL */
    termsOfUseUrl: string
    /** Contact page URL   */
    contactUrl?: string
  }
  /** Social media URLs */
  socials?: {
    /** Facebook page URL   */
    facebookUrl?: string
    /** LinkedIn page URL   */
    linkedinUrl?: string
    /** Instagram page URL   */
    instagramUrl?: string
    /** Share URL for the share functionality on the AP */
    shareUrl?: string
  }
  /**
   * The SAPI SDK gets initialized with a brand code, this is an override for said code, if none is provided
   * it will default to the brand's code
   * Reference: https://github.com/FindHotel/daedalus/blob/fcae3d9fd2e7a4bbf9f9ce47724cd7d3dfc454d9/packages/core/src/_web/brand/modules/selectors.ts#L47-L50
   * Use it if SAPI hasn't configured a profile for the brand yet or to test a different brand's configuration
   */
  sapiCode?: string
  /** Partner key which is used to determine the theme on Widgets, and also providing authentication for PAPI  */
  partnerKey?: string
  /** Main hotel image index, used to shift the main image on the Hotel Cards to avoid hotel content and results to look similar between differing brands */
  mainHotelImageIndex?: number
  /** Enabled filters in the QuickFilters bar on the RSP */
  enabledFilters?: FilterId[]
  /** Book on brand configuration */
  bookOnBrand?: {
    /** Whether book on brand functionality is enabled */
    enabled: boolean
    /** BoVio API key for production */
    bovioProdApiKey?: string
    /** BoVio API key for Staging */
    bovioStagingApiKey?: string
    /** Help center chat key */
    helpCenterChatKey?: string
    /** Default language code for the brand */
    defaultLanguageCode?: string
    /** Default currency code for the brand */
    defaultCurrencyCode?: string
  }
  /** Environment addresses */
  environmentAddresses?: {
    /** Staging environment address */
    stagingAddress?: string
    /** Staging checkout address */
    stagingCheckoutAddress?: string
    /** Staging booking management address */
    stagingBookingManagementAddress?: string
  }
  /** Locales where the customer service pages are available */
  customerServiceLocales?: string[]
  /** Whether the brand has a rectangular search form */
  hasAlternativeSearchForm?: boolean
  /** Cashback configuration */
  cashback?: {
    /** Whether cashback is enabled */
    enabled: boolean
  }
}
